import type { ListBuilderConfiguration } from '../types';

/**
 * Sensor Reading Log — device-scoped (PD-15734). Historical readings across
 * ALL of a device's modules, mounted under the Sensors list on the
 * merchandiser detail page (and reachable from the device detail). Fed by
 * `SensorDevicesService.findReadings` via a wrapQueryService adapter that
 * injects derived display fields (`distanceInches`, `temperatureF` is
 * already °F from the endpoint, `batteryVolts`, `statusLabel`) so the
 * columns read in U.S. customary units and a not-responding poll is named
 * instead of rendering as blanks.
 *
 * Filterable by timeframe (`sensorReadingTimeframe` — {startDate, endDate})
 * and module (`sensorReadingModule`); both resolve on the dedicated endpoint
 * (from/to/moduleId params), not through the generic SQL filter builder.
 */
export const SENSOR_READING_DEVICE_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-sensor-readings-device-embedded',
			cacheTTL: 60000,
			name: 'Reading Log',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'timestamp',
					fields: [
						{
							id: 'timestamp-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'readingAt' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: {
									dateFormat: 'datetime-medium',
									showTimeIfSet: true,
								},
							},
							displayStyle: 'datetime',
						},
					],
					headers: [{ id: 'h-timestamp', text: 'Date / Time' }],
					minWidth: 180,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'module',
					fields: [
						{
							id: 'module-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'moduleId' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-module', text: 'Module' }],
					minWidth: 80,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'status',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'statusLabel' },
							},
							styleConfig: {
								size: 'sm',
								valueMapping: [
									{
										value: 'Measured',
										label: 'Measured',
										color: { type: 'success' },
									},
									{
										value: 'Not responding',
										label: 'Not responding',
										color: { type: 'error' },
									},
								],
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h-status', text: 'Status' }],
					minWidth: 120,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'distance',
					fields: [
						{
							id: 'distance-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'distanceInches' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-distance', text: 'Depth (in)' }],
					minWidth: 100,
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'fill',
					fields: [
						{
							id: 'fill-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'fillPct' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-fill', text: 'Fill (%)' }],
					minWidth: 90,
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'temperature',
					fields: [
						{
							id: 'temperature-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'temperatureF' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-temp', text: 'Temp (°F)' }],
					minWidth: 100,
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'module-signal',
					fields: [
						{
							id: 'module-signal-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'moduleSignalStrength' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-signal', text: 'Signal' }],
					minWidth: 90,
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'battery',
					fields: [
						{
							id: 'battery-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'batteryVolts' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-battery', text: 'Battery (V)' }],
					minWidth: 100,
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
			],
			description: "The device's reading history, newest first",
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['sensorReadingTimeframe', 'sensorReadingModule'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/sensor-readings/:id',
			objectLabel: 'Sensor Reading',
			objectTable: 'sensor_reading',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'readingAt', fieldLabel: 'Date / Time' },
				{ fieldName: 'moduleId', fieldLabel: 'Module' },
				{ fieldName: 'statusLabel', fieldLabel: 'Status' },
				{ fieldName: 'distanceInches', fieldLabel: 'Depth (in)' },
				{ fieldName: 'fillPct', fieldLabel: 'Fill (%)' },
				{ fieldName: 'temperatureF', fieldLabel: 'Temp (°F)' },
				{ fieldName: 'moduleSignalStrength', fieldLabel: 'Signal' },
				{ fieldName: 'batteryVolts', fieldLabel: 'Battery (V)' },
			],
		},
	],
};
